/* The coverage grid. Colour is never the only signal (F-040): each of the six states has its own
   fill, its own pattern *and* its own glyph, and the word is in the legend and the accessible name.

     submitted     solid green-tint     check
     declared nil  solid blue-tint      minus in a circle
     missing       HATCHED amber        warning
     pending       DASHED outline       clock
     unknown       DOTTED grey          question mark
     not expected  HOLLOW               minus

   Every colour is a token already measured in tokens.css (the ok / info / warn tints are the
   StatusPill pairs). The glyph and word sit on a small solid face, so a pattern behind them never
   costs the text its contrast. */

.cell {
  --st-bg: var(--panel);
  --st-line: var(--line);
  --st-ink: var(--muted);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 28px;
  padding: 2px 4px;
  border: 1px solid var(--st-line);
  border-radius: 5px;
  background: var(--st-bg);
  color: var(--st-ink);
  font: inherit;
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: anywhere;
}

/* The face carries the glyph and the word on a solid ground. */
.face {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 3px;
  border-radius: 4px;
  background: var(--st-bg);
  color: var(--st-ink);
}

.submitted {
  --st-bg: var(--ok-w);
  --st-line: var(--ok-line);
  --st-ink: var(--ok);
}

.declared_nil {
  --st-bg: var(--info-w);
  --st-line: var(--info-line);
  --st-ink: var(--info);
}

.missing {
  --st-bg: var(--warn-w);
  --st-line: var(--warn);
  --st-ink: var(--warn);

  background: repeating-linear-gradient(135deg, var(--warn-w) 0 5px, var(--warn-line) 5px 7px);
}

.pending {
  --st-bg: var(--panel-2);
  --st-line: var(--ink-2);
  --st-ink: var(--ink-2);

  border-style: dashed;
}

.unknown {
  --st-bg: var(--panel-2);
  --st-line: var(--ink-2);
  --st-ink: var(--ink-2);

  border-style: dotted;
  background: radial-gradient(var(--line-2) 1px, transparent 1.6px) 0 0 / 6px 6px, var(--panel-2);
}

.not_expected {
  --st-bg: var(--panel);
  --st-line: var(--line-2);
  --st-ink: var(--muted);
}

/* The pattern states keep their face solid; the cell itself shows the pattern. */
.missing .face,
.unknown .face {
  background: var(--st-bg);
}

/* ------------------------------------------------------------------------------------ legend */

.legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px 18px;
  margin: 0 0 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.legendItem {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.legendItem .cell {
  flex: none;
  min-width: 96px;
}

.legendText {
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

/* ----------------------------------------------------------------------------------- controls */

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 10px;
}

.group {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel);
  color: var(--ink-2);
  font: inherit;
  font-size: var(--t-dense);
  cursor: pointer;
}

.toggle:hover {
  border-color: var(--accent);
}

.toggle[aria-pressed="true"] {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
  font-weight: 600;
}

.count {
  font-variant-numeric: lining-nums tabular-nums;
}

/* -------------------------------------------------------------------------------------- grid */

/* The box scrolls, never the page: a year of months is wider than a phone. */
.scroll {
  overflow-x: auto;
  max-width: 100%;
}

.grid {
  --name-w: 200px;
  --col-w: 38px;

  min-width: calc(var(--name-w) + var(--n) * var(--col-w));
}

.row {
  display: grid;
  grid-template-columns: var(--name-w) repeat(var(--n), minmax(var(--col-w), 1fr));
  column-gap: 2px;
  align-items: stretch;
  padding: 2px 0;
}

.row + .row {
  border-top: 1px solid var(--line);
}

.corner,
.month {
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
  text-align: center;
}

.corner {
  grid-column: 1;
  grid-row: 1;
  text-align: left;
}

.month {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-variant-numeric: lining-nums tabular-nums;
}

.year {
  font-weight: 500;
}

.name {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding-right: 8px;
}

.streamName {
  color: var(--ink);
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.streamMeta {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 400;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.slot {
  display: flex;
  min-width: 0;
}

.slot > .cell {
  position: relative;
  flex: 1;
  cursor: pointer;
}

/* A document may cover this window and is waiting for review (ADR-0166). A mark in the corner, in
 * ink and with a shape, so it is not a colour and does not touch the state's fill, pattern or glyph.
 * The words are in the cell's accessible name; this is decoration. */
.waitMark {
  position: absolute;
  top: 1px;
  right: 1px;
  padding: 1px;
  border-radius: 50%;
  background: var(--panel);
  color: var(--ink);
}

.waitStatus {
  display: grid;
  gap: 4px;
  margin: 0 0 10px;
  color: var(--muted);
  font-size: var(--t-min);
}

.waitLine {
  margin: 0;
  overflow-wrap: anywhere;
}

.waitWords {
  display: block;
  color: var(--muted);
}

.cell:focus-visible {
  position: relative;
  z-index: 1;
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* The window whose detail is open: a heavier ring, in ink, so it is not a colour. */
.picked {
  box-shadow: 0 0 0 2px var(--ink);
}

/* ---------------------------------------------------------------------------------------- table */

.table {
  width: 100%;
  border-collapse: collapse;
  color: var(--ink-2);
  font-size: var(--t-min);
}

.table th,
.table td {
  padding: 8px 6px;
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
  overflow-wrap: anywhere;
}

.table thead th {
  color: var(--muted);
  font-weight: 500;
}

.table tbody th {
  color: var(--ink);
  font-weight: 600;
}

.noteCell {
  min-width: 14ch;
}

/* ------------------------------------------------------------------------------------- detail */

.detail {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
}

.detail > * + * {
  margin-top: 8px;
}

.detailHead {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.detailHead .cell {
  flex: none;
}

.detailTitle {
  color: var(--ink);
  font-size: var(--t-dense);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.detailText,
.hint,
.ceiling {
  margin: 0;
  max-width: 92ch;
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.hint,
.ceiling {
  color: var(--muted);
}

.ceiling {
  margin-top: 10px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
}

.primary {
  padding: 6px 12px;
  border: 1px solid var(--accent);
  border-radius: var(--radius-ctl);
  background: var(--accent);
  color: var(--on-fill);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  cursor: pointer;
}

.primary:hover {
  background: var(--accent-h);
}

.open {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--t-dense);
  text-decoration: underline;
  cursor: pointer;
}

@media (width <= 600px) {
  .grid {
    --name-w: 150px;
  }

  .legend {
    grid-template-columns: 1fr;
  }
}
